import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { isEduEmail } from '../utils/berkeleyEmailAccess';
import {
  firestoreTimestampToMs,
  submitEduEmailForVerification,
} from '../utils/eduEmailVerification';
import { useEduVerifyResendCooldown } from './useEduVerifyResendCooldown';

/**
 * Gate community actions on a verified .edu profile (`users/{uid}.berkeleyEmailVerified`).
 * Shows the school-email popup when verification is missing or pending.
 */
export function useEduProfileVerification(currentUser) {
  const [berkeleyEmailVerified, setBerkeleyEmailVerified] = useState(false);
  const [userBerkeleyEmail, setUserBerkeleyEmail] = useState(null);
  const [verifiedAddress, setVerifiedAddress] = useState(null);
  const [verifyLastSentMs, setVerifyLastSentMs] = useState(0);
  const [verifySendFailedMs, setVerifySendFailedMs] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const { remainingSeconds: resendCooldownSeconds, markSent, clearLocalCooldown } =
    useEduVerifyResendCooldown(verifyLastSentMs);

  useEffect(() => {
    if (!currentUser?.uid) {
      setBerkeleyEmailVerified(false);
      setUserBerkeleyEmail(null);
      setVerifiedAddress(null);
      setVerifyLastSentMs(0);
      setVerifySendFailedMs(0);
      return undefined;
    }
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      const data = snap.data();
      setUserBerkeleyEmail(data?.berkeleyEmail ?? null);
      setBerkeleyEmailVerified(data?.berkeleyEmailVerified === true);
      setVerifiedAddress(data?.berkeleyEmailVerifiedAddress ?? null);
      setVerifyLastSentMs(firestoreTimestampToMs(data?.berkeleyEmailVerifyLastSentAt));
      setVerifySendFailedMs(firestoreTimestampToMs(data?.berkeleyEmailVerifySendFailedAt));
    });
    return () => unsub();
  }, [currentUser?.uid]);

  // The server stamps `berkeleyEmailVerifySendFailedAt` when Postmark refused the message
  // (after its own retry). A failure newer than the last successful send means the inbox
  // never got a link; lift the optimistic cooldown so the user can resend right away.
  const sendFailed = verifySendFailedMs > 0 && verifySendFailedMs > verifyLastSentMs;
  useEffect(() => {
    if (sendFailed) clearLocalCooldown();
  }, [sendFailed, clearLocalCooldown]);

  // Email ownership is proven when the confirmed address matches the submitted
  // .edu, or when the .edu is the account email and Firebase Auth has it verified
  // (OAuth / email-link / set-password). Mirrors onBerkeleyVerificationCreated.
  const berkeleyEmailLc = (userBerkeleyEmail || '').trim().toLowerCase();
  const authEmailLc = (currentUser?.email || '').trim().toLowerCase();
  const authEmailVerified = currentUser?.emailVerified === true;
  const verifiedAddressLc = (verifiedAddress || '').trim().toLowerCase();
  const emailConfirmed =
    Boolean(berkeleyEmailLc) &&
    ((verifiedAddressLc && verifiedAddressLc === berkeleyEmailLc) ||
      (authEmailVerified && authEmailLc && authEmailLc === berkeleyEmailLc));

  const closePopup = useCallback(() => {
    setShowPopup(false);
    setSubmitted(false);
    setPending(false);
  }, []);

  const ensureVerifiedProfile = useCallback(async () => {
    if (!currentUser?.uid) return false;
    if (berkeleyEmailVerified) return true;

    const authEmail = (currentUser?.email || '').trim().toLowerCase();
    const hasSubmittedEdu = Boolean(userBerkeleyEmail) || isEduEmail(authEmail);

    if (hasSubmittedEdu) {
      setPending(true);
      setSubmitted(false);
    } else {
      setEmailInput('');
      setEmailError('');
      setSubmitted(false);
      setPending(false);
    }
    setShowPopup(true);
    return false;
  }, [berkeleyEmailVerified, currentUser, userBerkeleyEmail]);

  const submitEduEmail = useCallback(async (overrideEmail) => {
    const fromArg = typeof overrideEmail === 'string' ? overrideEmail : undefined;
    const email = String(fromArg ?? emailInput).trim().toLowerCase();
    if (!email) {
      setEmailError('Please enter your .edu email.');
      return false;
    }
    if (!isEduEmail(email)) {
      setEmailError('Email must be a .edu address.');
      return false;
    }
    if (!currentUser?.uid) {
      setEmailError('Please sign in first.');
      return false;
    }
    setSubmitting(true);
    setEmailError('');
    try {
      const saved = await submitEduEmailForVerification({
        uid: currentUser.uid,
        accountEmail: currentUser.email || null,
        berkeleyEmail: email,
        source: 'reach_out',
      });
      setUserBerkeleyEmail(saved);
      setSubmitted(true);
      setPending(false);
      markSent();
      return true;
    } catch (err) {
      console.error('Error saving school email for verification:', err);
      setEmailError(err?.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [currentUser, emailInput, markSent]);

  const resendEduEmail = useCallback(async () => {
    if (resendCooldownSeconds > 0) return false;
    const email = (
      userBerkeleyEmail ||
      (isEduEmail(currentUser?.email || '') ? currentUser.email : '') ||
      ''
    )
      .trim()
      .toLowerCase();
    if (!email || !isEduEmail(email) || !currentUser?.uid) return false;
    setSubmitting(true);
    setEmailError('');
    try {
      await submitEduEmailForVerification({
        uid: currentUser.uid,
        accountEmail: currentUser.email || null,
        berkeleyEmail: email,
        source: 'resend',
      });
      setSubmitted(true);
      markSent();
      return true;
    } catch (err) {
      console.error('Error resending school email verification:', err);
      setEmailError(err?.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [currentUser, markSent, resendCooldownSeconds, userBerkeleyEmail]);

  return {
    berkeleyEmailVerified,
    showPopup,
    closePopup,
    ensureVerifiedProfile,
    emailInput,
    setEmailInput,
    emailError,
    setEmailError,
    submitting,
    submitted,
    pending,
    emailConfirmed,
    sendFailed,
    submittedEmail:
      userBerkeleyEmail ||
      (isEduEmail(currentUser?.email || '') ? currentUser.email : '') ||
      '',
    submitEduEmail,
    resendEduEmail,
    resendCooldownSeconds,
  };
}
